import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useMemo } from 'react';
import { CollaborateTaskData } from 'types/api/collaborate';
import { Page } from 'types/api/site.ts';
import { Skeleton } from 'ui';
import useGetPageId from '../../../hooks/use-get-pageid';
import { useAppSelector } from '../../../store';
import { getActiveTask } from '../../../store/presenceSlice';
import { getLoadingAIComments, getLoadingAiDesignReview, getSelectedTaskId, getTasks } from '../../../store/tasksSlice';
import { AIReviewTaskLoading } from './ai-review-loading';
import { NoTaskAdded } from './no-task-added';
import { Task } from './task';
interface Props {
  pages?: Page[];
  isLoadingTasks: boolean;
}

export const Tasks = ({ pages, isLoadingTasks }: Props) => {
  const activeTask = useAppSelector(getActiveTask);
  const selectedTaskId = useAppSelector(getSelectedTaskId);
  const loadingAiComments = useAppSelector(getLoadingAIComments);
  const loadingAiDesignReview = useAppSelector(getLoadingAiDesignReview);
  const { pageId } = useGetPageId();
  const tasks = useAppSelector(getTasks);

  // Determine if we should show skeleton (loading OR no success yet)
  const shouldShowSkeleton = isLoadingTasks;

  const filteredTasks = useMemo(
    () => tasks.filter(task => !task.task_id?.includes('PENDING')),
    [tasks]
  ) as Partial<CollaborateTaskData>[];

  // Only show "no tasks" if we've successfully loaded and have no tasks
  const hasNoTasks = !isLoadingTasks && filteredTasks.length === 0;

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: -20 },
    show: { opacity: 1, y: 0 }
  };

  // Generate stable keys for tasks
  const getStableKey = useCallback((task: Partial<CollaborateTaskData>) => {
    // For optimistic tasks, use task_id to ensure uniqueness during transitions
    if (task.task_id?.includes('OPTIMISTIC')) {
      return `sidebar-task-${task.task_id}`;
    }
    // Use comment_id as the stable identifier for real tasks
    if (task.task_comment_id) {
      return `sidebar-task-${task.task_comment_id}`;
    }
    // Fallback to task_id for edge cases
    return `sidebar-task-${task.task_id}`;
  }, []);

  const isGeneratingAiDesignReview = pageId ? loadingAiDesignReview[pageId] : null;
  const showDesignReviewIndicator = isGeneratingAiDesignReview === 'running' || isGeneratingAiDesignReview === 'pending';

  return (
    <div className="flex flex-col gap-2 overflow-y-auto relative" style={{ maxHeight: 500 }}>
      {showDesignReviewIndicator && <AIReviewTaskLoading />}
      <AnimatePresence>
        {shouldShowSkeleton ? (
          <motion.div
            className="flex flex-col gap-2 overflow-hidden relative"
            initial="hidden"
            animate="show"
            variants={container}
          >
            {Array(3).fill(0).map((_, i) => (
              <motion.div key={`skeleton-${i}`} variants={item}>
                <TasksSkeleton />
              </motion.div>
            ))
            }
          </motion.div>
        ) : hasNoTasks ? (
          <NoTaskAdded />
        ) : (
          filteredTasks.map((task: Partial<CollaborateTaskData>, index: number) => {
            const isGenerating = !!(task.task_id && loadingAiComments[task.task_id]);
            const isLoading = isGenerating && task.task_id !== selectedTaskId;
            return (
              <Task
                task={task}
                key={getStableKey(task)}
                index={index}
                pages={pages}
                isActiveSticker={activeTask?.taskId === task.task_id || isLoading}
                isGenerating={isGenerating}
              />
            );
          })
        )}
      </AnimatePresence>
    </div>
  );
};

export const TasksSkeleton = () => {
  return (
    <Skeleton className='flex h-28 w-full gap-2 rounded-lg border p-2 bg-background relative'>
      <div className='flex h-full w-8 flex-col items-center justify-between'>
        <div className='relative'>
          <Skeleton className='h-8 w-8 rounded-[15%_50%_50%_50%] p-1' />
        </div>
      </div>
      <div className='flex h-full flex-1 flex-col gap-2 overflow-hidden text-left'>
        <Skeleton className='h-5 w-full' />
        <Skeleton className='h-5 w-full' />
        <div className='mt-auto flex justify-between'>
          <div className='flex w-full items-center justify-between gap-1 overflow-hidden'>
            <Skeleton className='h-6 w-20' />
            <div className='flex gap-1'>
              <Skeleton className='h-6 w-6' />
              <Skeleton className='h-6 w-6' />
              <Skeleton className='h-6 w-6' />
            </div>
          </div>
        </div>
      </div>
    </Skeleton>
  )
}